import React, { useState, useEffect, useCallback } from 'react';

interface TutorialTooltipProps {
  tutorialId: string;
  title: string;
  content: React.ReactNode;
  targetRef: React.RefObject<HTMLElement>;
  position?: 'top' | 'bottom' | 'left' | 'right';
  onDismiss?: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai';

const TutorialTooltip: React.FC<TutorialTooltipProps> = ({
  tutorialId,
  title,
  content,
  targetRef,
  position = 'bottom',
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [, setIsDismissed] = useState(true);
  const [showHelp, setShowHelp] = useState(false);

  // Check if tutorial has been dismissed
  useEffect(() => {
    const checkTutorialStatus = async () => {
      try {
        const token = localStorage.getItem('account-token');
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/api/user/tutorial-status/${tutorialId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (!data.dismissed) {
            setIsDismissed(false);
            setIsVisible(true);
          } else {
            setShowHelp(true); // Show help icon if previously dismissed
          }
        }
      } catch (error) {
        console.error('Error checking tutorial status:', error);
      }
    };

    checkTutorialStatus();
  }, [tutorialId]);

  const handleDismiss = useCallback(async () => {
    try {
      const token = localStorage.getItem('account-token');
      if (token) {
        await fetch(`${API_BASE_URL}/api/user/tutorial-dismissed/${tutorialId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Error dismissing tutorial:', error);
    }

    setIsVisible(false);
    setIsDismissed(true);
    setShowHelp(true);
    onDismiss?.();
  }, [tutorialId, onDismiss]);

  const handleShowTutorial = () => {
    setIsVisible(true);
    setShowHelp(false);
  };

  if (!targetRef.current) return null;

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-cyan-700',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-cyan-700',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-cyan-700',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-cyan-700'
  };

  return (
    <>
      {/* Help icon to re-trigger tutorial */}
      {showHelp && (
        <button
          onClick={handleShowTutorial}
          className="absolute -top-2 -right-2 w-6 h-6 bg-cyan-700 text-white rounded-full flex items-center justify-center text-sm font-bold hover:bg-cyan-800 transition-colors z-10"
          title="Show help"
        >
          ?
        </button>
      )}

      {/* Tutorial tooltip */}
      {isVisible && (
        <div className={`absolute ${positionClasses[position]} z-50 w-80`}>
          {/* Arrow */}
          <div className={`absolute ${arrowClasses[position]} w-0 h-0`} />
          
          {/* Content */}
          <div className="bg-cyan-700 text-white rounded-lg shadow-xl p-4 animate-fade-in">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h4 className="font-semibold text-lg flex items-center gap-2">
                <span className="text-2xl">👋</span>
                {title}
              </h4>
              <button
                onClick={handleDismiss}
                className="text-white/80 hover:text-white p-1"
                title="Dismiss"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="text-white/90 text-sm">
              {content}
            </div>
            <button
              onClick={handleDismiss}
              className="mt-3 w-full bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TutorialTooltip;

